<script setup lang="ts">
import { onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { NxDropdown } from '../Dropdown';

const props = withDefaults(defineProps<{ disabled?: boolean; allowFlip?: boolean }>(), {
  disabled: false,
  allowFlip: true,
});

const emit = defineEmits<{ open: []; close: [] }>();

const isOpen = ref<boolean>(false);
const triggerRef = useTemplateRef('trigger-ref');
const popoverRef = useTemplateRef('popover-ref');

function close() {
  isOpen.value = false;
}

function closeOnEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.stopImmediatePropagation();
    isOpen.value = false;
  }
}

function closeOnClickOutside(e: MouseEvent) {
  if (
    triggerRef.value?.contains(e.target as Node) ||
    popoverRef.value?.contains(e.target as Node)
  ) {
    return;
  }
  isOpen.value = false;
}

function removeListeners() {
  document.removeEventListener('keydown', closeOnEsc, true);
  document.removeEventListener('click', closeOnClickOutside, true);
}
onUnmounted(removeListeners);
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) close();
  }
);
watch(isOpen, (newIsOpen) => {
  if (newIsOpen) {
    emit('open');
    document.addEventListener('keydown', closeOnEsc, true);
    document.addEventListener('click', closeOnClickOutside, true);
  } else {
    emit('close');
    removeListeners();
  }
});
</script>

<template>
  <NxDropdown :is-open="isOpen" align="center" fit-width @close="isOpen = false">
    <template #trigger>
      <div
        ref="trigger-ref"
        class="dropdown-area-wrapper"
        :style="{ cursor: props.disabled ? 'not-allowed' : undefined }"
        @click="
          () => {
            if (!props.disabled) {
              isOpen = !isOpen;
            }
          }
        "
      >
        <slot></slot>
      </div>
    </template>
    <div ref="popover-ref" class="dropdown-area" @click.stop>
      <slot name="area" v-bind="{ close }"></slot>
    </div>
  </NxDropdown>
</template>

<style lang="css" scoped>
.dropdown-area-wrapper {
  position: relative;
  width: fit-content;
  height: fit-content;
}

.dropdown-area {
  box-sizing: border-box;
  padding: var(--nxt-space-xs);
  background-color: var(--nxt-popover-bg);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-sm);
  box-shadow: var(--nxt-popover-shadow);
}
</style>
